/**
 * De opname voor het energielabel (NTA 8800, basismethode): wat de inspecteur
 * ter plaatse vastlegt. Alles begint leeg - de app verzint geen bouwjaar,
 * oppervlak, waarde of label.
 *
 * Het label zelf berekent en registreert de inspecteur in een eigen rekenkern en
 * in EP-online; deze app doet dat niet. Het draadformaat v1 kent nog geen
 * entiteit voor deze opname, dus wat hier staat blijft op dit apparaat.
 */

/** Waar een waarde vandaan komt: gemeten, met bewijs, of de forfaitaire waarde uit de norm. */
export const EVIDENCE_SOURCES = ['vastgesteld', 'bewijs', 'forfaitair'] as const;

export type EvidenceSource = (typeof EVIDENCE_SOURCES)[number];

export function isEvidenceSource(value: string): value is EvidenceSource {
    return (EVIDENCE_SOURCES as readonly string[]).includes(value);
}

export const SHELL_PARTS = ['dak', 'gevel', 'vloer', 'beglazing'] as const;

export type ShellPart = (typeof SHELL_PARTS)[number];

export interface ShellEntry {
    construction: string;
    /** Rc- of U-waarde zoals de inspecteur hem noteert, bijvoorbeeld `Rc 1,3`. */
    value: string;
    source: EvidenceSource | '';
}

export interface EnergyDraft {
    bouwjaar: string;
    gebruiksoppervlak: string;
    verliesoppervlak: string;
    verwarming: string;
    ventilatie: string;
    zonnepanelen: string;
    tapwater: string;
    schil: Record<ShellPart, ShellEntry>;
}

export const HEATING_OPTIONS = [
    'HR-ketel',
    'Warmtepomp',
    'Hybride warmtepomp',
    'Stadsverwarming',
    'Anders',
] as const;

export const VENTILATION_OPTIONS = [
    'Natuurlijke ventilatie',
    'Mechanische afvoer',
    'Balansventilatie met WTW',
    'Anders',
] as const;

export const WATER_OPTIONS = [
    'Combiketel',
    'Elektrische boiler',
    'Warmtepompboiler',
    'Anders',
] as const;

const EMPTY_SHELL_ENTRY: ShellEntry = { construction: '', value: '', source: '' };

export const DEFAULT_ENERGY_DRAFT: EnergyDraft = {
    bouwjaar: '',
    gebruiksoppervlak: '',
    verliesoppervlak: '',
    verwarming: '',
    ventilatie: '',
    zonnepanelen: '',
    tapwater: '',
    schil: {
        dak: EMPTY_SHELL_ENTRY,
        gevel: EMPTY_SHELL_ENTRY,
        vloer: EMPTY_SHELL_ENTRY,
        beglazing: EMPTY_SHELL_ENTRY,
    },
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function textOf(value: unknown): string {
    return typeof value === 'string' ? value : '';
}

/** Leest een bewaard concept; een onbekend of kapot veld wordt leeg, nooit een voorbeeldwaarde. */
export function readEnergyDraft(stored: unknown): EnergyDraft {
    if (!isRecord(stored)) {
        return DEFAULT_ENERGY_DRAFT;
    }

    const shell = isRecord(stored.schil) ? stored.schil : {};

    return {
        bouwjaar: textOf(stored.bouwjaar),
        gebruiksoppervlak: textOf(stored.gebruiksoppervlak),
        verliesoppervlak: textOf(stored.verliesoppervlak),
        verwarming: textOf(stored.verwarming),
        ventilatie: textOf(stored.ventilatie),
        zonnepanelen: textOf(stored.zonnepanelen),
        tapwater: textOf(stored.tapwater),
        schil: Object.fromEntries(
            SHELL_PARTS.map((part) => {
                const entry = isRecord(shell[part]) ? shell[part] : {};
                const source = textOf(entry.source);

                return [
                    part,
                    {
                        construction: textOf(entry.construction),
                        value: textOf(entry.value),
                        source: isEvidenceSource(source) ? source : '',
                    },
                ];
            }),
        ) as Record<ShellPart, ShellEntry>,
    };
}

/** `vastgesteld` en `bewijs` zijn groen, `forfaitair` is amber. */
export const evidenceTone: Record<EvidenceSource, 'accent' | 'warn'> = {
    vastgesteld: 'accent',
    bewijs: 'accent',
    forfaitair: 'warn',
};
